import {type MediaCardMetadata} from '@wandb/weave/common/types/media';
import {useMemo} from 'react';

import {getMediaMetadata} from '../../../util/media';
import {type RunWithHistoryAndMediaWandb} from '../../MediaCard';
import {type MediaBrowserPanelConfig} from '../common';

/**
 * Hook to get representative media metadata for sizing/layout decisions.
 *
 * This is separate from step calculation because it serves a different purpose:
 * - Step calculation is about slider/navigation state
 * - This is about "what metadata best represents the content being displayed"
 *
 * For different modes:
 * - Compare mode: Uses first column's configuration to get representative step/run
 *   (simulates what layoutConfig.getStep(0,0) and layoutConfig.getRun(0,0) would return)
 * - Other modes: Uses global current step with first run (original behavior)
 */
export function useRepresentativeMediaMetadata(
  runsWithMedia: RunWithHistoryAndMediaWandb[],
  mediaKeys: string[] | undefined,
  currentStep: number,
  config: Pick<MediaBrowserPanelConfig, 'mode' | 'compareSettings'>
): MediaCardMetadata | null {
  return useMemo(() => {
    const firstMediaKey = mediaKeys?.[0] ?? null;

    if (firstMediaKey == null || runsWithMedia.length === 0) {
      return null;
    }

    const {mode, compareSettings} = config;

    let representativeRun = runsWithMedia[0];
    let representativeStep = currentStep;

    // For compare mode, determine the step/run for the first cell (0,0)
    // This simulates what layoutConfig.getStep(0,0) and layoutConfig.getRun(0,0) would return
    if (mode === 'compare') {
      const firstColumn = compareSettings?.columns?.[0];

      // Get the run for the first column: use column's run, or shared run (linked), or first in list.
      const columnRunName =
        firstColumn?.run ?? compareSettings?.runName ?? undefined;
      const columnRun = columnRunName
        ? runsWithMedia.find(run => run.name === columnRunName)
        : undefined;
      representativeRun = columnRun ?? runsWithMedia[0];
      representativeStep = firstColumn?.step ?? currentStep;
    } else {
      representativeRun = runsWithMedia[0];
      representativeStep = currentStep;
    }

    const firstHistory = representativeRun?.history ?? null;
    const metadata =
      firstHistory != null
        ? getMediaMetadata(firstHistory, firstMediaKey, representativeStep)
        : null;

    return metadata ?? null;
  }, [runsWithMedia, mediaKeys, currentStep, config]);
}
